import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ListPage } from '@/components/templates/list-page'
import {
  ListPager,
  STAFF_LIST_PAGE_SIZE,
  totalDescription,
  useCursorPages,
  type PagedResponse,
} from '@/components/templates/list-pager'
import { Alert } from '@/components/ui/alert'
import { EmptyState } from '@/components/ui/empty-state'
import { ApiError, patchJSON } from '@/lib/api'
import {
  contactsExportPath,
  contactsPath,
  hasContactFilters,
  matchingFromFilters,
} from '@/lib/contacts/paths'
import type { Contact, ContactFilters } from '@/lib/contacts/types'
import { ContactsBulkSetFieldDialog } from '@/pages/ContactsBulkSetFieldDialog'
import {
  ContactsBulkChapterDialog,
  BulkChapterNotice,
  type BulkChapterAction,
  type BulkChapterChangeResponse,
} from '@/pages/ContactsBulkChapterDialog'
import {
  ContactsBulkGroupDialog,
  BulkGroupNotice,
  type BulkGroupAction,
  type BulkGroupChangeResponse,
} from '@/pages/ContactsBulkGroupDialog'
import { ContactsTable } from '@/pages/ContactsTable'
import { ContactsToolbar } from '@/pages/ContactsToolbar'
import { BulkLifecycleToolbar } from '@/pages/BulkLifecycleToolbar'
export { BulkLifecycleToolbar } from '@/pages/BulkLifecycleToolbar'

interface ContactsResponse extends PagedResponse {
  contacts: Contact[]
}

type BulkLifecycleAction = 'archive' | 'restore'

interface BulkLifecycleSkipped {
  contact_id: number
  contact_name: string
  reason: string
}

interface BulkLifecycleResponse {
  bulk_lifecycle: {
    selected_count: number
    changed_count: number
    skipped: BulkLifecycleSkipped[]
  }
}

interface BulkResult {
  action: BulkLifecycleAction
  changedCount: number
  skipped: BulkLifecycleSkipped[]
}

interface BulkLifecyclePayload {
  action: BulkLifecycleAction
  confirm: boolean
  contact_ids?: number[]
  matching?: Record<string, string | boolean>
}

export function ContactsListPage({
  message,
  onCreateContact,
  onOpenPerson,
  onOpenOrganization,
  onEditContact,
}: {
  message: string | null
  onCreateContact: () => void
  onOpenPerson: (contact: Contact) => void
  onOpenOrganization: (contact: Contact) => void
  onEditContact: (contact: Contact) => void
}) {
  const [setFieldOpen, setSetFieldOpen] = React.useState(false)
  const [groupAction, setGroupAction] = React.useState<BulkGroupAction | null>(null)
  const [chapterAction, setChapterAction] = React.useState<BulkChapterAction | null>(null)
  const [groupResult, setGroupResult] = React.useState<
    BulkGroupChangeResponse['bulk_group_change'] | null
  >(null)
  const [chapterResult, setChapterResult] = React.useState<
    BulkChapterChangeResponse['bulk_chapter_change'] | null
  >(null)
  const [filters, setFilters] = React.useState<ContactFilters>({
    search: '',
    contactType: 'all',
    marketingOptOut: 'all',
    includeArchived: false,
  })
  const { contactsQuery, pager, visibleContacts, totalCount } = useContactsPage(filters)
  const bulkLifecycle = useBulkLifecycleSelection(filters, visibleContacts, totalCount)
  const hasFilters = hasContactFilters(filters)

  function updateFilters(nextFilters: ContactFilters) {
    setFilters(nextFilters)
    bulkLifecycle.reset()
  }

  return (
    <ListPage
      title="Contacts"
      description={totalDescription(totalCount, 'Contact', 'Contacts', hasFilters)}
      primaryAction={{ label: 'New contact', onClick: onCreateContact }}
      search={{
        value: filters.search,
        onChange: (search) => updateFilters({ ...filters, search }),
        placeholder: 'Search by name or email...',
      }}
      toolbar={<ContactsToolbar filters={filters} onChange={updateFilters} />}
    >
      {message && <Alert className="m-4">{message}</Alert>}
      <BulkLifecycleNotice result={bulkLifecycle.result} error={bulkLifecycle.error} />
      <BulkGroupNotice result={groupResult} />
      <BulkChapterNotice result={chapterResult} />
      {contactsQuery.isLoading && <EmptyState title="Loading Contacts" />}
      {contactsQuery.isError && <EmptyState title="Contacts unavailable" />}
      {contactsQuery.isSuccess && contactsQuery.data.contacts.length === 0 && (
        <EmptyState title={hasFilters ? 'No matching Contacts' : 'No Contacts yet'} />
      )}
      {contactsQuery.isSuccess && contactsQuery.data.contacts.length > 0 && (
        <>
          <BulkLifecycleToolbar
            selectedCount={bulkLifecycle.selectedCount}
            hasSelection={bulkLifecycle.hasSelection}
            isPending={bulkLifecycle.isPending}
            exportHref={contactsExportPath(filters, bulkLifecycle.exportContactIds)}
            onSelectMatching={bulkLifecycle.selectAllMatching}
            onSetField={() => setSetFieldOpen(true)}
            onGroupChange={setGroupAction}
            onChapterChange={setChapterAction}
            onArchive={() => bulkLifecycle.run('archive')}
          />
          <ListPager pager={pager} label="Contacts" position="top" />
          <ContactsTable
            contacts={visibleContacts}
            selectedIds={bulkLifecycle.selectedIdsForTable}
            allVisibleSelected={bulkLifecycle.allVisibleSelected}
            onToggleContact={bulkLifecycle.toggleContact}
            onToggleVisible={bulkLifecycle.toggleVisible}
            onOpenPerson={onOpenPerson}
            onOpenOrganization={onOpenOrganization}
            onEditContact={onEditContact}
          />
          <ListPager pager={pager} label="Contacts" position="bottom" />
          <ContactsBulkSetFieldDialog
            open={setFieldOpen}
            onOpenChange={setSetFieldOpen}
            contactIds={bulkLifecycle.selectedContactIds}
            onApplied={bulkLifecycle.completeBulkFieldUpdate}
          />
          <ContactsBulkGroupDialog
            open={groupAction !== null}
            onOpenChange={(open) => {
              if (!open) setGroupAction(null)
            }}
            action={groupAction}
            selection={bulkLifecycle.groupSelection}
            selectedCount={bulkLifecycle.selectedCount}
            onApplied={(response) => {
              setGroupResult(response.bulk_group_change)
              bulkLifecycle.clearSelection()
            }}
          />
          <ContactsBulkChapterDialog
            open={chapterAction !== null}
            onOpenChange={(open) => {
              if (!open) setChapterAction(null)
            }}
            action={chapterAction}
            selection={bulkLifecycle.chapterSelection}
            selectedCount={bulkLifecycle.selectedCount}
            onApplied={(response) => {
              setChapterResult(response.bulk_chapter_change)
              bulkLifecycle.clearSelection()
            }}
          />
        </>
      )}
    </ListPage>
  )
}

function useContactsPage(filters: ContactFilters) {
  const { query: contactsQuery, pager } = useCursorPages<ContactsResponse>({
    queryKey: ['contacts'],
    path: contactsPath(filters),
    limit: STAFF_LIST_PAGE_SIZE,
  })
  return {
    contactsQuery,
    pager,
    visibleContacts: contactsQuery.data?.contacts ?? [],
    totalCount: contactsQuery.data?.total_count,
  }
}

function BulkLifecycleNotice({
  result,
  error,
}: {
  result: BulkResult | null
  error: string | null
}) {
  if (error) return <Alert className="m-4">{error}</Alert>
  if (!result) return null

  return (
    <Alert className="m-4">
      <div>{bulkResultMessage(result)}</div>
      {result.skipped.length > 0 && (
        <ul className="mt-2 space-y-1">
          {result.skipped.map((skipped) => (
            <li key={skipped.contact_id}>
              {skipped.contact_name}: {skipped.reason}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  )
}

// `totalCount` is the server's count of every Contact matching the filters, which is what
// "select all matching" selects; the page on screen is only a slice of it.
function useBulkLifecycleSelection(
  filters: ContactFilters,
  visibleContacts: Contact[],
  totalCount: number | undefined,
) {
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [selectMatching, setSelectMatching] = React.useState(false)
  const [result, setResult] = React.useState<BulkResult | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const queryClient = useQueryClient()
  const selectedIdsForTable = selectMatching ? idsForContacts(visibleContacts) : selectedIds
  const exportContactIds =
    !selectMatching && selectedIds.size > 0 ? sortedIds(selectedIds) : undefined
  const selectedContactIds = sortedIds(selectedIds)
  const groupSelection = selectMatching
    ? { matching: matchingFromFilters(filters) }
    : { contact_ids: selectedContactIds }
  const chapterSelection = groupSelection
  const allVisibleSelected =
    selectMatching ||
    (visibleContacts.length > 0 && visibleContacts.every((contact) => selectedIds.has(contact.id)))
  const selectedCount = selectMatching ? (totalCount ?? visibleContacts.length) : selectedIds.size
  const hasSelection = selectedCount > 0
  const mutation = useMutation({
    mutationFn: (action: BulkLifecycleAction) => runBulkLifecycle(action, window.confirm),
    onSuccess: (response, action) => {
      if (!response) return

      setResult(bulkResultFromResponse(action, response))
      setError(null)
      clearSelection()
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: (mutationError) => {
      setResult(null)
      setError(mutationError instanceof Error ? mutationError.message : 'Bulk update failed.')
    },
  })

  function reset() {
    setResult(null)
    setError(null)
    clearSelection()
  }

  function clearSelection() {
    setSelectedIds(new Set())
    setSelectMatching(false)
  }

  function clearFeedback() {
    setResult(null)
    setError(null)
  }

  function toggleVisible(checked: boolean) {
    clearFeedback()
    setSelectMatching(false)
    setSelectedIds(checked ? idsForContacts(visibleContacts) : new Set())
  }

  function toggleContact(contact: Contact) {
    clearFeedback()
    setSelectedIds((currentIds) =>
      toggledContactIds(currentIds, contact, selectMatching, visibleContacts),
    )
    setSelectMatching(false)
  }

  function selectAllMatching() {
    clearFeedback()
    setSelectedIds(new Set())
    setSelectMatching(true)
  }

  function completeBulkFieldUpdate() {
    clearSelection()
    void queryClient.invalidateQueries({ queryKey: ['contacts'] })
  }

  async function runBulkLifecycle(
    action: BulkLifecycleAction,
    confirmSelection: (message?: string) => boolean,
  ) {
    const preview = await previewBulkLifecycle(bulkLifecyclePayload(action, false))
    const confirmed = confirmSelection(
      `${bulkActionLabel(action)} ${preview.bulk_lifecycle.selected_count} selected Contacts?`,
    )
    if (!confirmed) return null

    return patchJSON<BulkLifecycleResponse>('/api/v1/contacts/bulk_lifecycle', {
      bulk_lifecycle: bulkLifecyclePayload(action, true),
    })
  }

  function bulkLifecyclePayload(
    action: BulkLifecycleAction,
    confirmed: boolean,
  ): BulkLifecyclePayload {
    return selectMatching
      ? { action, matching: matchingFromFilters(filters), confirm: confirmed }
      : { action, contact_ids: sortedIds(selectedIds), confirm: confirmed }
  }

  return {
    result,
    error,
    selectedIdsForTable,
    exportContactIds,
    selectedContactIds,
    groupSelection,
    chapterSelection,
    allVisibleSelected,
    selectedCount,
    hasSelection,
    isPending: mutation.isPending,
    reset,
    clearSelection,
    toggleContact,
    toggleVisible,
    selectAllMatching,
    completeBulkFieldUpdate,
    run: mutation.mutate,
  }
}

function idsForContacts(contacts: Contact[]) {
  return new Set(contacts.map((contact) => contact.id))
}

function sortedIds(ids: Set<number>) {
  return Array.from(ids).sort((left, right) => left - right)
}

function toggledContactIds(
  currentIds: Set<number>,
  contact: Contact,
  selectMatching: boolean,
  visibleContacts: Contact[],
) {
  const nextIds = selectMatching ? idsForContacts(visibleContacts) : new Set(currentIds)

  if (nextIds.has(contact.id)) {
    nextIds.delete(contact.id)
  } else {
    nextIds.add(contact.id)
  }

  return nextIds
}

async function previewBulkLifecycle(
  bulkLifecycle: BulkLifecyclePayload,
): Promise<BulkLifecycleResponse> {
  try {
    return await patchJSON<BulkLifecycleResponse>('/api/v1/contacts/bulk_lifecycle', {
      bulk_lifecycle: bulkLifecycle,
    })
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return error.body as BulkLifecycleResponse
    }
    throw error
  }
}

function bulkResultFromResponse(action: BulkLifecycleAction, response: BulkLifecycleResponse) {
  return {
    action,
    changedCount: response.bulk_lifecycle.changed_count,
    skipped: response.bulk_lifecycle.skipped,
  }
}

function bulkActionLabel(action: BulkLifecycleAction) {
  return action === 'archive' ? 'Archive' : 'Restore'
}

function bulkResultMessage(result: BulkResult) {
  return `${bulkActionLabel(result.action)} complete: ${result.changedCount} changed, ${result.skipped.length} skipped.`
}
